import { format, isValid, parseISO } from 'date-fns';
import type { Project } from '@/api/client';
import { coverCdnUrl, parseCoverCode } from '@covers/projectCover';
import { getBaseStatus, getSubstatus, isCompletedStatus, type ProjectStatus } from '@/utils/projectStatuses';
import { PROJECT_TYPES, PROJECT_TYPE_DISPLAY, getProjectTypeColor, type ProjectTypeSymbolValue, type ProjectTypeValue } from '@/utils/projectTypeColors';
import {
  getDayWordRu,
  getDeadlineDaysLeft,
  getDeadlineUrgencyTier,
  getRemainingHeadlineRu,
} from '@/utils/projectDeadlineRemaining';
import { PROJECT_STAGES } from '@stages/stages';
import { FLAG_STAGE_DONE, getFlagStage } from './projectFlags';

/** Этапы в порядке работы над проектом (слева направо на шкале этапов) — общий контракт этапов. */
export const STAGE_PIPELINE = PROJECT_STAGES;

/** Порядок групп в списке «В работе» — как в таблице (sortProjects): Проверка правок сверху, Добавлен снизу. */
export const ACTIVE_LIST_STAGE_ORDER = ['Проверка правок', 'Правки', 'Проверка', 'Монтаж', 'Запись', 'Добавлен'] as const satisfies readonly ProjectStatus[];

/** Порядок групп в поиске по этапам: находятся и готовые — они последними (как sort=stage на сервере). */
export const SEARCH_LIST_STAGE_ORDER = [...ACTIVE_LIST_STAGE_ORDER, 'Готов'] as const satisfies readonly ProjectStatus[];

export type StageKey = 'added' | 'recording' | 'montage' | 'review' | 'revisions' | 'crreview' | 'done';

const STAGE_KEY: Record<ProjectStatus, StageKey> = {
  Добавлен: 'added',
  Запись: 'recording',
  Монтаж: 'montage',
  Проверка: 'review',
  Правки: 'revisions',
  'Проверка правок': 'crreview',
  Готов: 'done',
};

export function toProjectStatus(state: string | null | undefined): ProjectStatus {
  const base = getBaseStatus(state);
  return (STAGE_PIPELINE as readonly string[]).includes(base) ? (base as ProjectStatus) : 'Добавлен';
}

export function getStageKey(state: string | null | undefined): StageKey {
  return STAGE_KEY[toProjectStatus(state)];
}

/** CSS-переменная цвета этапа (см. newdesign.css). */
export function stageColorVar(stage: StageKey): string {
  return `var(--stage-${stage})`;
}

/** Заливка цветом этапа на карточке — 16 % (полоса шапки, плашка срока) */
export function stageTint(stage: StageKey): string {
  return `color-mix(in oklab, ${stageColorVar(stage)} 16%, var(--card))`;
}

export interface StageView {
  status: ProjectStatus;
  key: StageKey;
  /** Подстатус («В работе») или null */
  substatus: string | null;
  /**
   * Для «Записи» без подстатуса — средний прогресс чтецов, иначе null. «Запись: Завершена» — null: виден подстатус.
   * При удалённой записи (УЗ зелёная) — null: у нас не пишется, процента нет.
   */
  recordingProgress: number | null;
  /** Подстатус завершающий («Монтаж: Завершён», «Правки: Внесены»…) */
  completed: boolean;
}

export function getStageView(project: Pick<Project, 'state' | 'project_remote'>, progress: number): StageView {
  const status = toProjectStatus(project.state);
  const remote = getFlagStage(project, 'project_remote') === FLAG_STAGE_DONE;
  const substatus = getSubstatus(project.state);
  return {
    status,
    key: STAGE_KEY[status],
    substatus,
    recordingProgress: status === 'Запись' && substatus === null && !remote ? progress : null,
    completed: isCompletedStatus(project.state),
  };
}

/**
 * Текст бейджа подстатуса в строке списка: процент записи или подстатус с большой буквы; этап пишется в корешке группы.
 * withStage — когда корешка этапа нет (по срочности, по алфавиту): «Правки · Подготовлены», без подстатуса — просто этап.
 * null — бейджа нет.
 */
export function getStageChipLabel(stage: StageView, { withStage = false }: { withStage?: boolean } = {}): string | null {
  const detail =
    stage.recordingProgress !== null
      ? `${stage.recordingProgress}%`
      : stage.substatus
        ? capitalize(stage.substatus.toLowerCase())
        : null;
  if (!withStage) return detail;
  return detail ? `${stage.status} · ${detail}` : stage.status;
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export interface TypeMarkView {
  /** Знак типа, как в таблице и расписании: «А», «Az», «П», «★» или иконка (хлопушка у Аудиоспектакля) */
  label: ProjectTypeSymbolValue;
  /** «Авторский», «Подкаст»… */
  text: string;
  /** Цвет типа (projectColorTokens) */
  color: string;
}

/** Значок типа (шапка карточки, меню типа, фильтр): у любого известного типа, «Не авторский» — «•», как в таблице; пустой и неизвестный — null. */
export function getTypeMark(type: string | null | undefined): TypeMarkView | null {
  const key = type?.trim();
  if (!key || !(PROJECT_TYPES as readonly string[]).includes(key)) return null;
  const display = PROJECT_TYPE_DISPLAY[key as ProjectTypeValue];
  return { label: display.label, text: display.text, color: getProjectTypeColor(key) };
}

/** Значок типа для строки списка; у обычных (Common) значка нет — в списке он отмечает только особенные проекты. */
export function getListTypeMark(type: string | null | undefined): TypeMarkView | null {
  return type?.trim() === 'Common' ? null : getTypeMark(type);
}

/** Полная обложка на CDN Эксмо по коду ASTA — для просмотра; null — кода нет или он не похож на код (защита URL). */
export function getProjectCoverUrl(asta: string | null | undefined): string | null {
  const code = parseCoverCode(asta);
  return code ? coverCdnUrl(code) : null;
}

export type DeadlineTone = 'urgent' | 'warning' | 'calm' | 'done';

export interface DeadlineView {
  /** dd.MM.yyyy */
  date: string;
  /** dd.MM */
  shortDate: string;
  daysLeft: number;
  tone: DeadlineTone;
  /** «Просрочен на 3 дня», «Дедлайн сегодня», «Осталось 5 дней» */
  text: string;
}

/** Пороги — те же, что у пилюли дедлайна в таблице (getDeadlineUrgencyTier). */
export function getDeadlineView(deadline: string | null | undefined, isCompleted: boolean): DeadlineView | null {
  const daysLeft = getDeadlineDaysLeft(deadline);
  if (daysLeft === null || !deadline) return null;
  const d = parseISO(deadline);
  if (!isValid(d)) return null;
  const tier = getDeadlineUrgencyTier(daysLeft, isCompleted);
  const tone: DeadlineTone =
    tier === 'complete' ? 'done' : tier === 'urgent' ? 'urgent' : tier === 'warning' ? 'warning' : 'calm';
  const text =
    daysLeft < 0
      ? `Просрочен на ${Math.abs(daysLeft)} ${getDayWordRu(daysLeft)}`
      : daysLeft === 0
        ? 'Дедлайн сегодня'
        : `${getRemainingHeadlineRu(daysLeft)} ${daysLeft} ${getDayWordRu(daysLeft)}`;
  return { date: format(d, 'dd.MM.yyyy'), shortDate: format(d, 'dd.MM'), daysLeft, tone, text };
}

/** «Анна, Иван +2». Пустые имена пропускаются. */
export function formatNamesShort(names: readonly (string | null | undefined)[], max: number): string {
  const clean = names.map((n) => n?.trim()).filter((n): n is string => !!n);
  if (clean.length <= max) return clean.join(', ');
  return `${clean.slice(0, max).join(', ')} +${clean.length - max}`;
}

/** dd.MM.yyyy; null — пусто или невалидно. */
export function formatFullDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = parseISO(iso);
  return isValid(d) ? format(d, 'dd.MM.yyyy') : null;
}

export interface StageGroup<T> {
  status: ProjectStatus;
  items: T[];
}

/** Группы по этапам в заданном порядке; порядок внутри группы сохраняется, пустые группы отбрасываются. */
export function groupByStage<T>(
  items: readonly T[],
  getState: (item: T) => string | null | undefined,
  order: readonly ProjectStatus[]
): StageGroup<T>[] {
  const buckets = new Map<ProjectStatus, T[]>(order.map((s) => [s, []]));
  for (const item of items) {
    const status = toProjectStatus(getState(item));
    const bucket = buckets.get(status);
    if (bucket) bucket.push(item);
  }
  return order.map((status) => ({ status, items: buckets.get(status) ?? [] })).filter((g) => g.items.length > 0);
}

/** Подряд идущие элементы одного этапа — полосы этапа там, где список не сгруппирован (поиск, готовые, архив). */
export function groupStageRuns<T>(items: readonly T[], getState: (item: T) => string | null | undefined): StageGroup<T>[] {
  const runs: StageGroup<T>[] = [];
  for (const item of items) {
    const status = toProjectStatus(getState(item));
    const last = runs.at(-1);
    if (last?.status === status) last.items.push(item);
    else runs.push({ status, items: [item] });
  }
  return runs;
}
